/**
 * BookingBadge — shows a flight's booking status (none / held / booked).
 *
 * "held" carries an optional hold-expiry countdown (e.g. AA's 24h hold). The
 * status is honest: absent/none reads "Not booked" rather than implying any
 * action we haven't taken.
 *
 * The price shown is PER PERSON, because it sits inches from the focus card's
 * per-person fare and a total next to a per-person figure reads as a fare that
 * jumped. The party total is on the booked-flight note beneath.
 */
import { Check, Clock } from "lucide-react";

interface Props {
  status?: "none" | "held" | "booked";
  /** ISO timestamp a held fare expires; renders "Xh left" when in the future. */
  holdExpiresAt?: string | null;
  /** One traveller's share of what was paid. */
  pricePaidPerPerson?: number | null;
  /** What the party paid — surfaced as a tooltip so the badge stays one line. */
  pricePaidTotal?: number | null;
}

function hoursLeft(iso?: string | null): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso) - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;
  return Math.max(1, Math.round(ms / 3_600_000));
}

function money(n: number): string {
  return "$" + Math.round(n).toLocaleString();
}

export default function BookingBadge({
  status = "none",
  holdExpiresAt,
  pricePaidPerPerson,
  pricePaidTotal,
}: Props) {
  if (status === "booked") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
        title={pricePaidTotal != null ? `${money(pricePaidTotal)} paid in total` : undefined}
      >
        <Check className="h-3 w-3" aria-hidden="true" />
        {pricePaidPerPerson != null ? (
          <>
            Paid · <span className="font-mono">{money(pricePaidPerPerson)}</span> pp
          </>
        ) : (
          "Booked"
        )}
      </span>
    );
  }
  if (status === "held") {
    const h = hoursLeft(holdExpiresAt);
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
        <Clock className="h-3 w-3" aria-hidden="true" />
        {h != null ? (
          <>
            Held · <span className="font-mono">{h}h</span> left
          </>
        ) : (
          "Held"
        )}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
      Not booked
    </span>
  );
}
